import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiSearch, FiBookOpen, FiUsers, FiCalendar, FiRefreshCcw } from 'react-icons/fi';
import apiService from '../../services/api';

const ElectivesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 2.5rem;
  font-weight: 500;
  margin: 0;
  letter-spacing: -0.025em;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const SearchBar = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  
  @media (max-width: 768px) {
    max-width: none;
  }
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  background: var(--primary-color);
  color: var(--text-inverse);
  border: 1px solid var(--primary-color);
  border-radius: 0;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: var(--transition);
  -webkit-tap-highlight-color: rgba(0,0,0,0);

  &:hover {
    background: var(--primary-hover);
    border-color: var(--primary-hover);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: 0;
  font-size: 0.875rem;
  background-color: var(--background-primary);
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
`;

const ElectivesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ElectiveCard = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  transition: var(--transition);
  cursor: pointer;
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const ElectiveHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ElectiveTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
`;

const ElectiveCode = styled.p`
  color: var(--text-muted);
  font-size: 0.875rem;
  margin: 0 0 1rem 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
`;

const ElectiveStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  
  @media (min-width: 640px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
  }
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const ElectiveContent = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ElectiveDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
`;

const ElectiveActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  
  &:hover {
    background-color: var(--background-secondary);
    color: var(--text-primary);
  }
  
  &.primary {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    
    &:hover {
      background-color: var(--primary-light);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  
  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h3 {
    color: var(--text-primary);
    margin-bottom: 0.5rem;
  }
`;

function Electives() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [electives, setElectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [authError, setAuthError] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  
  const fetchElectives = useCallback(async (isRefresh = false) => {
    try {
      if (!isRefresh) {
        setLoading(true);
      }
      
      console.log('🔄 Fetching electives...');
      const response = await apiService.getElectives();
      console.log('📚 Electives response:', response);
      
      if (response.success && response.electives) {
        console.log(`✅ Loaded ${response.electives.length} electives`);
        setElectives(response.electives);
        setRetryCount(0);
        setAuthError(false);
        setFetchError(false);
      } else {
        console.warn('⚠️ No electives found in response:', response);
        setElectives([]);
        setFetchError(false);
      }
    } catch (error) {
      console.error('❌ Error fetching electives:', error);
      
      if (error.status === 401 || error.status === 403) {
        console.log('🔐 Authentication error detected, forcing logout - no retries');
        setElectives([]);
        setRetryCount(0);
        setAuthError(true);
        return;
      } else {
        setFetchError(true);
        if (retryCount < 2 && !isRefresh) {
          console.log(`🔄 Retrying elective fetch (attempt ${retryCount + 1}/2) in ${1000 * (retryCount + 1)}ms...`);
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            fetchElectives(false);
          }, 1000 * (retryCount + 1));
        } else {
          console.error('❌ Max retries reached, giving up');
          setElectives([]);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  useEffect(() => {
    fetchElectives();
  }, [fetchElectives]);

  useEffect(() => {
    if (location.state?.refresh) {
      fetchElectives(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, fetchElectives, navigate, location.pathname]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (electives.length > 0) {
        console.log('🔄 Periodic refresh of electives...');
        fetchElectives(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchElectives, electives.length]);

  const handleElectiveClick = (electiveId) => {
    navigate(`/electives/${electiveId}`);
  };

  const handleManualRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    setRetryCount(0);
    setAuthError(false);
    setFetchError(false);
    fetchElectives(false);
  };

  const formatStartDate = (startDate) => {
    if (!startDate) return 'TBD';
    
    const date = new Date(startDate);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const electiveDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (electiveDate <= today) {
      return 'Started';
    }
    
    const options = { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    };
    
    return date.toLocaleDateString('en-US', options);
  };

  const filteredElectives = electives.filter(elective => {
    // Get all instructor names
    const instructorNames = elective.instructors && Array.isArray(elective.instructors) && elective.instructors.length > 0
      ? elective.instructors.map(inst => `${inst.first_name} ${inst.last_name}`).join(' ')
      : `${elective.instructor_first_name || ''} ${elective.instructor_last_name || ''}`.trim();
    
    const matchesSearch = elective.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         elective.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (elective.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         instructorNames.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  return (
    <ElectivesContainer>
      <Header>
        <TitleRow>
          <Title>My Electives</Title>
          {fetchError && !loading && (
            <RefreshButton onClick={handleManualRefresh}>
              <FiRefreshCcw size={16} />
              Refresh
            </RefreshButton>
          )}
        </TitleRow>
        <SearchBar>
          <SearchIcon>
            <FiSearch size={16} />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search electives..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBar>
      </Header>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <FiBookOpen size={32} />
          <p>Loading electives...</p>
        </div>
      ) : filteredElectives.length > 0 ? (
        <ElectivesGrid>
          {filteredElectives.map((elective, index) => (
            <ElectiveCard
              key={elective.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              onClick={() => handleElectiveClick(elective.id)}
            >
              <ElectiveHeader>
                <ElectiveTitle>{elective.name}</ElectiveTitle>
                <ElectiveCode>
                  {elective.code} • {
                    elective.instructors && Array.isArray(elective.instructors) && elective.instructors.length > 0
                      ? elective.instructors.map(inst => `${inst.first_name} ${inst.last_name}`).join(', ')
                      : `${elective.instructor_first_name || ''} ${elective.instructor_last_name || ''}`.trim() || 'N/A'
                  }
                </ElectiveCode>
                <ElectiveStats>
                  <Stat>
                    <FiUsers size={14} />
                    {elective.enrolled_students || 0} students
                  </Stat>
                  <Stat>
                    <FiCalendar size={14} />
                    {formatStartDate(elective.start_date)}
                  </Stat>
                </ElectiveStats>
              </ElectiveHeader>
              
              <ElectiveContent>
                <ElectiveDescription>{elective.description}</ElectiveDescription>
                
                <ElectiveActions>
                  <ActionButton className="primary">View Elective</ActionButton>
                </ElectiveActions>
              </ElectiveContent>
            </ElectiveCard>
          ))}
        </ElectivesGrid>
      ) : (
        <EmptyState>
          <FiBookOpen />
          <h3>No electives found</h3>
          {authError ? (
            <p>Your session has expired. You will be redirected to login shortly.</p>
          ) : (
            <>
              <p>Try adjusting your search to find what you're looking for.</p>
            </>
          )}
        </EmptyState>
      )}
    </ElectivesContainer>
  );
}

export default Electives;

